import React from 'react';
import {
  Typography, Divider, Row, Col, Input, Space, Button, Table, Switch,
} from 'antd';
import { PlusOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import SubirImagen from './SubirImagen';

const { Title, Text, Paragraph } = Typography;


/**
 * Los TEXTOS del sitio: el hero, la sección informativa y los links del menú.
 *
 * Existían todos en `DEFAULT_CATALOGO_CONFIG` desde el primer día y no había
 * dónde cargarlos: el hero mostraba el nombre del catálogo porque `hero_titulo`
 * estaba vacío y el productor no tenía forma de tocarlo ni de sacarlo, y la
 * sección informativa —que en la plantilla OP-1 va justo arriba de los toros—
 * no se podía completar por ningún lado.
 *
 * Van juntos y separados del diseño a propósito: elegir colores y escribir los
 * textos de la página son dos tareas distintas, y se hacen en momentos
 * distintos.
 */
const ContenidosTab = ({ config = {}, onChange, onSubirImagen, onQuitarImagen }) => {
  const links = config.menu_links || [];

  const cambiarLink = (indice, clave, valor) => {
    const copia = links.map((l, i) => (i === indice ? { ...l, [clave]: valor } : l));
    onChange('menu_links', copia);
  };

  const moverLink = (indice, destino) => {
    if (destino < 0 || destino >= links.length) return;
    const copia = [...links];
    const [fila] = copia.splice(indice, 1);
    copia.splice(destino, 0, fila);
    onChange('menu_links', copia);
  };

  return (
    <div>
      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <Title level={5} style={{ marginBottom: 4 }}>Textos del hero</Title>
      <Paragraph type="secondary" style={{ fontSize: 13 }}>
        Lo que se lee sobre la foto de apertura. Si dejás el título vacío se usa
        el nombre del catálogo; para que no aparezca ningún texto, vaciá los dos
        y el hero queda sólo con la imagen.
      </Paragraph>
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        <Input
          addonBefore="Título"
          placeholder="Ej: Genética Braford del norte santafesino"
          value={config.hero_titulo || ''}
          onChange={(e) => onChange('hero_titulo', e.target.value)}
        />
        <Input.TextArea
          rows={2}
          placeholder="Bajada (opcional)"
          value={config.hero_subtitulo || ''}
          onChange={(e) => onChange('hero_subtitulo', e.target.value)}
        />
      </Space>

      <Row gutter={[16, 12]} style={{ marginTop: 14 }}>
        <Col xs={24} md={12}>
          <Text strong style={{ fontSize: 13 }}>Botón principal</Text>
          <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 6 }}>
            <Input
              placeholder="Texto — vacío = el botón no se muestra"
              value={config.hero_btn_primario_texto || ''}
              onChange={(e) => onChange('hero_btn_primario_texto', e.target.value)}
            />
            <Input
              placeholder="Link (https://... o #toros)"
              value={config.hero_btn_primario_url || ''}
              onChange={(e) => onChange('hero_btn_primario_url', e.target.value)}
            />
          </Space>
        </Col>
        <Col xs={24} md={12}>
          <Text strong style={{ fontSize: 13 }}>Botón secundario</Text>
          <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 6 }}>
            <Input
              placeholder="Texto — vacío = el botón no se muestra"
              value={config.hero_btn_secundario_texto || ''}
              onChange={(e) => onChange('hero_btn_secundario_texto', e.target.value)}
            />
            <Input
              placeholder="Link (https://... o #hembras)"
              value={config.hero_btn_secundario_url || ''}
              onChange={(e) => onChange('hero_btn_secundario_url', e.target.value)}
            />
          </Space>
        </Col>
      </Row>

      <Divider />

      {/* ── Sección informativa ─────────────────────────────────────────── */}
      <Title level={5} style={{ marginBottom: 4 }}>Nuestra historia</Title>
      <Paragraph type="secondary" style={{ fontSize: 13 }}>
        El bloque de texto e imagen que va arriba de los animales. NO tiene un
        switch para prenderlo: aparece si lo completás y no aparece si no. Un
        interruptor aparte crea un segundo estado que se desincroniza — texto
        cargado, switch en No, y jurás que lo escribiste y no se ve.
      </Paragraph>
      <Row gutter={[24, 16]}>
        <Col xs={24} md={14}>
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Input
              addonBefore="Título"
              placeholder="Ej: 40 años seleccionando"
              value={config.info_titulo || ''}
              onChange={(e) => onChange('info_titulo', e.target.value)}
            />
            <Input.TextArea
              rows={4}
              placeholder="Primer párrafo"
              value={config.info_texto_1 || ''}
              onChange={(e) => onChange('info_texto_1', e.target.value)}
            />
            <Input.TextArea
              rows={4}
              placeholder="Segundo párrafo (opcional)"
              value={config.info_texto_2 || ''}
              onChange={(e) => onChange('info_texto_2', e.target.value)}
            />
          </Space>
        </Col>
        <Col xs={24} md={10}>
          <Text strong style={{ fontSize: 13 }}>Imagen</Text>
          <div style={{ marginTop: 6 }}>
            <SubirImagen
              valor={config.info_img}
              campo="info_img"
              onSubir={onSubirImagen}
              onQuitar={onQuitarImagen}
              alto={90}
              ayuda="Sin imagen, el texto ocupa el ancho completo."
            />
          </div>
        </Col>
      </Row>

      <Row gutter={[16, 12]} style={{ marginTop: 14 }}>
        <Col xs={24} md={12}>
          <Text strong style={{ fontSize: 13 }}>Botón principal</Text>
          <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 6 }}>
            <Input
              placeholder="Texto — vacío = el botón no se muestra"
              value={config.info_btn_primario_texto || ''}
              onChange={(e) => onChange('info_btn_primario_texto', e.target.value)}
            />
            <Input
              placeholder="Link"
              value={config.info_btn_primario_url || ''}
              onChange={(e) => onChange('info_btn_primario_url', e.target.value)}
            />
          </Space>
        </Col>
        <Col xs={24} md={12}>
          <Text strong style={{ fontSize: 13 }}>Botón secundario</Text>
          <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 6 }}>
            <Input
              placeholder="Texto — vacío = el botón no se muestra"
              value={config.info_btn_secundario_texto || ''}
              onChange={(e) => onChange('info_btn_secundario_texto', e.target.value)}
            />
            <Input
              placeholder="Link"
              value={config.info_btn_secundario_url || ''}
              onChange={(e) => onChange('info_btn_secundario_url', e.target.value)}
            />
          </Space>
        </Col>
      </Row>

      <Divider />

      {/* ── Links del menú ──────────────────────────────────────────────── */}
      <Title level={5} style={{ marginBottom: 4 }}>Links del menú</Title>
      <Paragraph type="secondary" style={{ fontSize: 13 }}>
        Se suman a las secciones de animales, a la derecha. Podés apuntar a otro
        sitio (<Text code>https://…</Text>) o a una sección de esta misma página
        (<Text code>#toros</Text>, <Text code>#hembras</Text>).
      </Paragraph>

      <Table
        size="small"
        rowKey={(_, i) => i}
        dataSource={links}
        pagination={false}
        locale={{ emptyText: 'Sin links propios: el menú muestra sólo tus secciones de animales' }}
        columns={[
          {
            title: 'Orden',
            width: 90,
            render: (_, __, i) => (
              <Space size={0}>
                <Button
                  size="small" type="text" icon={<ArrowUpOutlined />}
                  disabled={i === 0} onClick={() => moverLink(i, i - 1)}
                />
                <Button
                  size="small" type="text" icon={<ArrowDownOutlined />}
                  disabled={i === links.length - 1} onClick={() => moverLink(i, i + 1)}
                />
              </Space>
            ),
          },
          {
            title: 'Texto',
            render: (_, fila, i) => (
              <Input
                size="small"
                placeholder="Ej: Nuestra historia"
                value={fila.label || ''}
                onChange={(e) => cambiarLink(i, 'label', e.target.value)}
              />
            ),
          },
          {
            title: 'Link',
            render: (_, fila, i) => (
              <Input
                size="small"
                placeholder="https://… o #toros"
                value={fila.url || ''}
                onChange={(e) => cambiarLink(i, 'url', e.target.value)}
              />
            ),
          },
          {
            title: 'Nueva pestaña',
            width: 120,
            render: (_, fila, i) => (
              <Switch
                size="small"
                checked={!!fila.nueva_pestana}
                onChange={(v) => cambiarLink(i, 'nueva_pestana', v)}
              />
            ),
          },
          {
            title: '',
            width: 50,
            render: (_, __, i) => (
              <Button
                danger type="text" size="small" icon={<DeleteOutlined />}
                onClick={() => onChange('menu_links', links.filter((_l, j) => j !== i))}
              />
            ),
          },
        ]}
      />
      <Button
        icon={<PlusOutlined />}
        style={{ marginTop: 12 }}
        onClick={() => onChange('menu_links', [...links, { label: '', url: '' }])}
      >
        Agregar link
      </Button>
    </div>
  );
};

export default ContenidosTab;
